'use client';

import { useCommerceItem } from '@/context/CommerceItemContext';
import type { CommerceButtonRenderDetails } from '@/types/commerce';
import { useCommerceConfig } from '@/context/CommerceConfigContext';
import { type FC, useState, useEffect, type ReactNode } from 'react';
import { renderCommerceLink, buildOfferButtonText } from '@/utils/commerce';
import {
	SelectOfferActionType,
	selectMultipleOffersReducer,
	type SelectMultipleOffersState,
} from '@/components/Commerce/reducers';
import {
	type CommerceItem,
	type CommerceOffer,
	buildExternalOffer,
	type CtaEmbedConfig,
	isCompleteCommerceItem,
	OfferSelectionContainer,
	type CommerceEmbedSharedOptions,
} from '@themaven-net/commerce-shared';

export interface CommerceCtaProps {
	buttonTextOverride?: string;
	className?: string;
	config: CtaEmbedConfig;
	href?: string;
	phxTrackId?: string;
}

export const CommerceCta: FC<CommerceCtaProps> = ({
	buttonTextOverride,
	className,
	config,
	href,
	phxTrackId,
}) => {
	const commerceConfigContext = useCommerceConfig();
	const commerceItemContext = useCommerceItem();

	// @todo can this be refactored for all of the single item embeds?
	const selectOffers = (): CommerceOffer[] => {
		try {
			const offer = config?.offer ?? commerceItemContext.commerceOffer;
			if (offer !== undefined) {
				return [offer];
			}

			if (config?.link !== undefined) {
				return [buildExternalOffer(config.link)];
			}

			if (isCompleteCommerceItem(config?.item)) {
				const container = new OfferSelectionContainer(
					config.item?.offers ?? [],
					commerceConfigContext?.phxCommerce?.preferredMerchants ?? [],
					commerceConfigContext?.phxCommerce?.preferredAffiliates ?? [],
					3,
				);
				return container.selectedOffers ?? [];
			}
		} catch (e) {
			// eslint-disable-next-line no-console
			console.error('Encountered error while building or selecting offer', e);

			return [];
		}

		// eslint-disable-next-line no-console
		console.warn('Unable to select or build an offer for CommerceCta');
		return [];
	};

	const InitialOfferState: SelectMultipleOffersState = {
		offerMetadata: {
			preferredAffiliates:
				commerceConfigContext?.phxCommerce?.preferredAffiliates ?? [],
			preferredMerchants:
				commerceConfigContext?.phxCommerce?.preferredMerchants ?? [],
		},
		offers: selectOffers(),
	};
	// Offer effect
	const [offerState, setOfferState] =
		useState<SelectMultipleOffersState>(InitialOfferState);

	useEffect(() => {
		if (offerState.offers && offerState.primaryOffer === undefined) {
			const newOfferState = selectMultipleOffersReducer(offerState, {
				payload: { offers: offerState.offers },
				type: SelectOfferActionType.UPDATE_OFFER_SELECTION,
			});
			if (newOfferState.primaryOffer !== undefined) {
				setOfferState(newOfferState);
			}
		}
	}, [offerState, offerState.offers, offerState.primaryOffer]);

	const getButtonText = (): string => {
		// @todo Swap the string literal to a site setting
		const defaultButtonText = buttonTextOverride ?? `Shop It`;

		if (config?.ctaText !== undefined && config?.ctaText !== '') {
			// we should not do text replacement on manually specified text
			return config.ctaText;
		}

		if (
			offerState.primaryOffer &&
			config?.options?.buttonTitleByIdentifier !== undefined
		) {
			return buildOfferButtonText(
				offerState.primaryOffer,
				config.options,
				defaultButtonText,
			);
		}

		return defaultButtonText;
	};

	try {
		const { primaryOffer } = offerState;

		if (
			config === undefined ||
			(primaryOffer === undefined && href === undefined)
		) {
			return undefined;
		}

		const commerceItem = config.item ?? commerceItemContext.commerceItem;
		const buttonText = getButtonText();
		const item = isCompleteCommerceItem(commerceItem)
			? commerceItem
			: undefined;
		return renderCommerceLink(
			buttonText,
			primaryOffer,
			item,
			className,
			phxTrackId,
			config,
		);
	} catch (e) {
		return undefined;
	}
};

/**
 * Render a commerce cta element using the supplied config
 *
 * This method allows for easy addition of defaults to cta configs
 */
export const renderCommerceCta = (
	offerDetails: CommerceButtonRenderDetails,
	item: CommerceItem,
	embedOptions?: CommerceEmbedSharedOptions,
	classes?: string,
	phxTrackId?: string,
): ReactNode => {
	if (offerDetails === undefined || offerDetails.text === undefined) {
		return undefined;
	}

	return (
		<CommerceCta
			className={classes ?? ''}
			config={{
				ctaText: offerDetails.text,
				item,
				offer: offerDetails.offer,
				options: embedOptions,
			}}
			phxTrackId={phxTrackId}
		/>
	);
};
